import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { FirmwareUpdateModalComponent } from '../firmware-update-modal/firmware-update-modal.component';

import {
  Client,
  EditMeterDataIn, GuidDictionaryItem,
  MeterGeoLocationQueryDataOut,
  MeterModelDetailsDataOut,
  SetIpSecConnectorDataIn,
} from '../../../../api/api-reference';
import { PermissionsEnum } from '../../../../api/api-reference';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../../services/auth.service';
import { ClipboardService } from '../../../../services/clipboard.service';
import { AssetTypeBadgeComponent } from '../../components/asset-type-badge/asset-type-badge.component';
import { DeviceEditComponent } from './device-edit/device-edit.component';

@Component({
  selector: 'app-device-information',
  standalone: true,
  imports: [
    DatePipe,
    TranslateModule,
    DeviceEditComponent,
    AngularSvgIconModule,
    CommonModule,
    GoogleMapsModule,
    FirmwareUpdateModalComponent,
    AssetTypeBadgeComponent
  ],
  templateUrl: './device-information.component.html',
  styleUrl: './device-information.component.scss',
})
export class DeviceInformationComponent implements OnInit {
  @Input() deviceId: string = '';
  @Input() embedded = false;
  // @Input() lowLevelPwdChangeStatus: LowLevelPwdChangeCommandStatusDataOut | undefined;
  @Output() closeSignal = new EventEmitter<boolean>();
  device: EditMeterDataIn = new EditMeterDataIn();
  meterModel: MeterModelDetailsDataOut = new MeterModelDetailsDataOut();
  showEditModal = false;
  showMeterGeoLocationHistoryModal = false;
  isMapVisible = false;
  mapType: 'asset' | 'meter' = 'asset';
  mapCenter?: { lat: number; lng: number };
  public PermissionsEnum = PermissionsEnum;
  geoLocationHistory: MeterGeoLocationQueryDataOut[] = [];
  isFirmwareUpdateModalOpen = false;
  devicePreviousModel!: GuidDictionaryItem;

  constructor(
    private toastr: ToastrService,
    private client: Client,
    public authService: AuthService,
    public clipboardService: ClipboardService
  ) { }

  private loadGoogleMaps() {
    if (!document.querySelector(`script[src*="maps.googleapis.com"]`)) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.gmap_key}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }
  ngOnInit(): void {
    this.getDeviceById();
    this.loadGoogleMaps();
  }


  get canRollbackFirmware(): boolean {
    return !!this.device?.previousMeterModel;
  }

  setLocationViewed() {
    this.client.setMeterGeoLocationViewed(this.deviceId).subscribe({
      next: () => { },
      error: (err) => {
        this.toastr.error(err);
        this.closeSignal.emit();
      },
    });
  }

  showMeterMap(lat?: number, lng?: number) {
    this.mapType = 'meter';
    if (lat !== undefined && lng !== undefined) {
      this.mapCenter = { lat, lng };
    }
    this.isMapVisible = true;
    this.setLocationViewed();
  }

  showAssetMap() {
    this.mapType = 'asset';
    this.mapCenter = {
      lat: Number(this.device.serviceLatitude),
      lng: Number(this.device.serviceLongitude),
    };
    this.isMapVisible = true;
  }

  closeMap() {
    this.isMapVisible = false;
  }

  toggleModal() {
    if (this.embedded) {
      this.getDeviceById();
    } else {
      this.device = new EditMeterDataIn();
      this.meterModel = new MeterModelDetailsDataOut();
    }
    this.closeSignal.emit();
  }

  getDeviceById() {
    this.client.getDevice(this.deviceId).subscribe({
      next: (response) => {
        this.device = response;
        console.log(this.device);
        this.devicePreviousModel = new GuidDictionaryItem();
        this.devicePreviousModel.name = this.device.previousMeterModelName ? this.device.previousMeterModelName : "";
        this.devicePreviousModel.value = this.device.previousMeterModel ? this.device.previousMeterModel : "";
        this.getMeterModel();
        this.getMeterGeoLocationHistory();
      },
      error: (err) => {
        this.toastr.error(err);
        this.closeSignal.emit();
      },
    });
  }

  getMeterModel() {
    this.client.getMeterModel(this.device.meterModel!).subscribe({
      next: (response) => {
        this.meterModel = response;
      },
      error: (err) => {
        this.toastr.error(err);
        this.closeSignal.emit();
      },
    });
  }

  get firstGeoLocation(): MeterGeoLocationQueryDataOut | undefined {
    return this.geoLocationHistory.length > 0 ? this.geoLocationHistory[0] : undefined;
  }

  formattedGeoLocation(lat: number, lon: number): string {
    if (!lat || !lon) return '/';
    return `${lat}<br> ${lon}`;
  }

  getMeterGeoLocationHistory() {
    this.client.getMeterGeoLocation(this.deviceId).subscribe({
      next: (response) => {
        this.geoLocationHistory = response;
      },
      error: (err) => {
        this.toastr.error(err);
        this.closeSignal.emit();
      },
    });
  }

  edit() {
    this.showEditModal = true;
  }

  onEditClosed(): void {
    this.showEditModal = false;
    if (this.embedded) {
      this.getDeviceById();
    } else {
      this.toggleModal();
    }
  }

  showGeoHistory() {
    this.showMeterGeoLocationHistoryModal = true;
    this.setLocationViewed();
  }

  formatInterval(minutes: number): string {
    if (minutes >= 60) {
      const hours = minutes / 60;
      return `${hours}h`;
    } else {
      return `${minutes}mins`;
    }
  }

  toggleIpSec(isEnabled: boolean) {
    let ipSecData = new SetIpSecConnectorDataIn();
    ipSecData.meterNumber = this.device.meterNumber;
    ipSecData.activate = isEnabled;

    this.client.setIpSecConnector(ipSecData).subscribe({
      next: (response) => {
        if (response != null) {
          this.device.ipSecConnector = response.toString();
        } else {
          this.device.ipSecConnector = '/';
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onFirmwareUpdateClosed(): void {
    this.isFirmwareUpdateModalOpen = false;
    if (this.embedded) {
      this.getDeviceById();
    } else {
      this.toggleModal();
    }
  }
}
